import React, { useState } from 'react';
import { useSubmissionWorkflow } from '../hooks/useSubmissionWorkflow';
import { PhotoCaptureStep } from './submission/PhotoCaptureStep';
import { AIAnalysisStep } from './submission/AIAnalysisStep';
import { MetadataEntryStep } from './submission/MetadataEntryStep';
import { WorkflowErrorBoundary } from './WorkflowErrorBoundary';
import { SubmissionStep } from '../types/submission-workflow';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

interface MultiStepSubmissionFormProps {
  onSuccess?: (reportId: string) => void;
  onCancel?: () => void;
}

export const MultiStepSubmissionForm: React.FC<MultiStepSubmissionFormProps> = ({
  onSuccess,
  onCancel
}) => {
  const { isEnabled } = useFeatureFlag();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    workflow,
    isLoading,
    error,
    navigation,

    addPhoto,
    removePhoto,
    startAIAnalysis,
    retryAIAnalysis,
    skipAIAnalysis,
    updateMetadata,
    goToNextStep,
    goToPreviousStep,
    cancelWorkflow,
    submitWorkflow
  } = useSubmissionWorkflow();

  // Step configuration - dynamic based on feature flags
  const aiAnalysisEnabled = isEnabled('AI_ANALYSIS_ENABLED');
  const steps: { key: SubmissionStep; title: string; description: string }[] = [
    {
      key: 'photo_capture',
      title: 'Capture Photos',
      description: 'Take or upload photos of your treasure'
    },
    ...(aiAnalysisEnabled ? [{
      key: 'ai_analysis' as SubmissionStep,
      title: 'AI Analysis',
      description: 'Let AI analyze and suggest tags'
    }] : []),
    {
      key: 'metadata_entry',
      title: 'Add Details',
      description: 'Review and add location information'
    }
  ];

  // Handle workflow cancellation
  const handleCancel = () => {
    cancelWorkflow();
    onCancel?.();
  };

  // Handle workflow submission
  const handleSubmit = async () => {
    if (!navigation.canSubmit && workflow?.currentStep !== 'metadata_entry') return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await submitWorkflow();
      if (result?.success && result.reportId) {
        // Small delay to show success state
        await new Promise(resolve => setTimeout(resolve, 500));
        onSuccess?.(result.reportId);
      } else {
        throw new Error('Submission failed - no report ID returned');
      }
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Submission failed';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle next step - if on metadata step, submit instead
  const handleNextStep = async () => {
    if (workflow?.currentStep === 'metadata_entry') {
      await handleSubmit();
    } else {
      goToNextStep();
    }
  };

  // Handle retry for AI analysis
  const handleRetryAnalysis = () => {
    retryAIAnalysis();
  };

  if (!workflow || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">🔄</div>
          <p className="text-urban-300">
            {!workflow ? 'Initializing submission workflow...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <WorkflowErrorBoundary>
      <div className="min-h-screen bg-sky-neutral py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-urban mb-2">Submit a Treasure Report</h1>
          <p className="text-urban-300">
            Share your curbside find with the community through our guided process
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {steps.map((step, index) => {
              const isActive = workflow.currentStep === step.key;
              const isCompleted = navigation.currentStepIndex > index;
              const isAccessible = navigation.currentStepIndex >= index;

              return (
                <div key={step.key} className="flex items-center">
                  {/* Step Circle */}
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-medium text-sm ${
                    isCompleted
                      ? 'bg-verge border-verge text-white'
                      : isActive
                        ? 'bg-cardboard border-cardboard text-white'
                        : isAccessible
                          ? 'bg-white border-cardboard text-cardboard'
                          : 'bg-teddy-200 border-urban-300 text-urban-300'
                  }`}>
                    {isCompleted ? '✓' : index + 1}
                  </div>

                  {/* Step Info */}
                  <div className="ml-3 hidden sm:block">
                    <p className={`text-sm font-medium ${
                      isActive ? 'text-urban' : 'text-urban-300'
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-urban-200">{step.description}</p>
                  </div>

                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-4 ${
                      navigation.currentStepIndex > index ? 'bg-verge' : 'bg-urban-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Mobile Step Info */}
          <div className="sm:hidden text-center">
            <p className="text-sm font-medium text-urban">
              {steps[navigation.currentStepIndex]?.title}
            </p>
            <p className="text-xs text-urban-300">
              Step {navigation.currentStepIndex + 1} of {navigation.totalSteps}
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-card p-6 mb-8">
          {/* Submission Status */}
          {isSubmitting && (
            <div className="mb-6 bg-verge-50 border border-verge-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="animate-spin text-verge-500 text-xl">🔄</div>
                <div>
                  <h4 className="font-medium text-verge-800">Submitting Report</h4>
                  <p className="text-verge-700 mt-1">Creating your treasure report...</p>
                </div>
              </div>
            </div>
          )}

          {/* Global Error */}
          {(error || submitError) && !isSubmitting && (
            <div className="mb-6 bg-rust-50 border border-rust-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <span className="text-rust text-xl">⚠️</span>
                <div>
                  <h4 className="font-medium text-rust-800">Error</h4>
                  <p className="text-rust-700 mt-1">{error || submitError}</p>
                  {submitError && (
                    <button
                      onClick={() => setSubmitError(null)}
                      className="mt-2 text-sm text-rust-600 hover:text-rust-800 underline"
                    >
                      Dismiss
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step Content */}
          {workflow.currentStep === 'photo_capture' && (
            <PhotoCaptureStep
              photos={workflow.data.photos}
              onAddPhoto={addPhoto}
              onRemovePhoto={removePhoto}
              maxPhotos={5}
              maxFileSize={10 * 1024 * 1024}
            />
          )}

          {workflow.currentStep === 'ai_analysis' && aiAnalysisEnabled && (
            <AIAnalysisStep
              photos={workflow.data.photos}
              analysisData={workflow.data.aiAnalysis}
              onStartAnalysis={startAIAnalysis}
              onRetryAnalysis={handleRetryAnalysis}
              onSkipAnalysis={skipAIAnalysis}
              isLoading={isLoading}
            />
          )}

          {workflow.currentStep === 'metadata_entry' && workflow.data.metadata && (
            <MetadataEntryStep
              workflow={workflow}
              onUpdateMetadata={updateMetadata}
              onNext={handleNextStep}
              onPrevious={goToPreviousStep}
            />
          )}
        </div>

        {/* Navigation - Only show for non-metadata steps */}
        {workflow.currentStep !== 'metadata_entry' && (
          <div className="flex justify-between items-center">
            {/* Back Button */}
            <button
              onClick={goToPreviousStep}
              disabled={!navigation.canGoBack || isLoading}
              className="px-6 py-3 bg-teddy-300 text-urban rounded-lg font-medium hover:bg-teddy-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              ← Previous
            </button>

            {/* Cancel Button */}
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-urban-300 hover:text-urban transition-colors"
            >
              Cancel
            </button>

            {/* Next/Submit Button */}
            <div className="flex space-x-3">
              {navigation.canGoForward && !navigation.canSubmit && (
                <button
                  onClick={goToNextStep}
                  disabled={!navigation.canGoForward || isLoading}
                  className="px-6 py-3 bg-cardboard text-white rounded-lg font-medium hover:bg-cardboard-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <div className="animate-spin mr-2">🔄</div>
                      Processing...
                    </span>
                  ) : (
                    'Next →'
                  )}
                </button>
              )}

              {navigation.canSubmit && (
                <button
                  onClick={handleSubmit}
                  disabled={!navigation.canSubmit || isSubmitting}
                  className="px-6 py-3 bg-verge text-white rounded-lg font-medium hover:bg-verge-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <div className="animate-spin mr-2">🔄</div>
                      Submitting...
                    </span>
                  ) : (
                    '✓ Submit Report'
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Cancel button for metadata step */}
        {workflow.currentStep === 'metadata_entry' && (
          <div className="text-center mt-4">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-urban-300 hover:text-urban transition-colors"
            >
              Cancel Submission
            </button>
          </div>
        )}

        {/* Workflow Info */}
        <div className="mt-6 text-center text-xs text-urban-200">
          <p>
            Workflow ID: {workflow.id} • 
            Created: {workflow.createdAt instanceof Date ? workflow.createdAt.toLocaleTimeString() : 'Unknown'} • 
            Expires: {workflow.expiresAt instanceof Date ? workflow.expiresAt.toLocaleTimeString() : 'Unknown'}
          </p>
        </div>
        </div>
      </div>
    </WorkflowErrorBoundary>
  );
};